import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Loader2, Users, TrendingUp, CheckCircle, BarChart3 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SurveyAnalytics = () => {
  const { surveyId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchAnalytics();
    fetchResponses();
  }, [surveyId]);

  useEffect(() => {
    fetchResponses();
  }, [page]);

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/analytics/${surveyId}`, { withCredentials: true });
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/analytics/${surveyId}/responses?page=${page}`, { withCredentials: true });
      setResponses(data.responses);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  };

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/analytics/${surveyId}/export/csv`, '_blank');
  };

  const handleExportExcel = () => {
    window.open(`${API_URL}/api/analytics/${surveyId}/export/excel`, '_blank');
  };

  const handleExportPDF = () => {
    window.open(`${API_URL}/api/analytics/${surveyId}/export/pdf`, '_blank');
  };

  const COLORS = ['#7A8B7A', '#C67D63', '#5C635A', '#8E958C', '#2C302E'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-[#5C635A]">Failed to load analytics</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-[#5C635A] hover:text-[#2C302E]">
                <ArrowLeft size={20} />
              </Link>
              <h1 className="text-xl font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {analytics.survey_name}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-4 py-2 border border-[#E5E0D8] rounded-lg text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="export-csv-btn"
              >
                <Download size={18} />
                CSV
              </button>
              <button
                onClick={handleExportExcel}
                className="flex items-center gap-2 px-4 py-2 border border-[#E5E0D8] rounded-lg text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="export-excel-btn"
              >
                <Download size={18} />
                Excel
              </button>
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-4 py-2 bg-[#7A8B7A] text-white rounded-lg hover:bg-[#637363] transition-colors"
                data-testid="export-pdf-btn"
              >
                <Download size={18} />
                PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<Users className="text-[#7A8B7A]" />}
            label="Total Responses"
            value={analytics.total_responses}
          />
          <StatCard
            icon={<CheckCircle className="text-[#C67D63]" />}
            label="Completion Rate"
            value={`${analytics.completion_rate}%`}
          />
          <StatCard
            icon={<TrendingUp className="text-[#5C635A]" />}
            label="Questions"
            value={analytics.question_stats.length}
          />
          <StatCard
            icon={<BarChart3 className="text-[#8E958C]" />}
            label="Avg. Rating"
            value={
              analytics.question_stats
                .filter(q => q.type === 'star' && q.average)
                .map(q => q.average)[0] || 'N/A'
            }
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-[#E5E0D8]">
          {['overview', 'questions', 'responses'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-[#7A8B7A] text-[#2C302E]'
                  : 'border-transparent text-[#5C635A] hover:text-[#2C302E]'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-6">
            <h2 className="text-lg font-medium text-[#2C302E] mb-4">Responses Over Time (Last 30 Days)</h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.responses_over_time}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => format(parseISO(date), 'MMM d')}
                    stroke="#8E958C"
                    fontSize={12}
                  />
                  <YAxis stroke="#8E958C" fontSize={12} />
                  <Tooltip 
                    labelFormatter={(date) => format(parseISO(date), 'MMM d, yyyy')}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E5E0D8' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#7A8B7A" 
                    strokeWidth={2}
                    dot={{ fill: '#7A8B7A', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {activeTab === 'questions' && (
          <div className="space-y-6">
            {analytics.question_stats.map((stat, index) => (
              <div key={stat.question_id} className="bg-white rounded-xl border border-[#E5E0D8] p-6">
                <h3 className="text-lg font-medium text-[#2C302E] mb-2">
                  {index + 1}. {stat.question}
                </h3>
                <p className="text-sm text-[#8E958C] mb-4">
                  {stat.total_responses} responses • {stat.type}
                </p>

                {stat.type === 'star' && stat.average && (
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-3xl font-bold text-[#C67D63]">{stat.average}</div>
                    <div className="text-[#5C635A]">/ 5 average rating</div>
                  </div>
                )}

                {stat.type === 'checkbox' && stat.average_selections !== undefined && (
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-3xl font-bold text-[#C67D63]">{stat.average_selections}</div>
                    <div className="text-[#5C635A]">average selections per response</div>
                  </div>
                )}

                {stat.distribution && (
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      {stat.type === 'star' ? (
                        <BarChart data={Object.entries(stat.distribution).map(([k, v]) => ({ rating: `${k} Star${k !== '1' ? 's' : ''}`, count: v }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                          <XAxis dataKey="rating" stroke="#8E958C" fontSize={12} />
                          <YAxis stroke="#8E958C" fontSize={12} />
                          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E0D8' }} />
                          <Bar dataKey="count" fill="#C67D63" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      ) : (
                        <PieChart>
                          <Pie
                            data={Object.entries(stat.distribution).map(([k, v]) => ({ name: k, value: v }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            dataKey="value"
                          >
                            {Object.entries(stat.distribution).map((_, idx) => (
                              <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E0D8' }} />
                        </PieChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'responses' && (
          <div className="bg-white rounded-xl border border-[#E5E0D8] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F4F1EA]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[#5C635A]">Submitted</th>
                    {analytics.question_stats.map(q => (
                      <th key={q.question_id} className="px-4 py-3 text-left text-sm font-medium text-[#5C635A] max-w-[200px] truncate">
                        {q.question}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E0D8]">
                  {responses.map(response => (
                    <tr key={response.id}>
                      <td className="px-4 py-3 text-sm text-[#5C635A] whitespace-nowrap">
                        {format(parseISO(response.submitted_at), 'MMM d, yyyy h:mm a')}
                      </td>
                      {analytics.question_stats.map(q => (
                        <td key={q.question_id} className="px-4 py-3 text-sm text-[#2C302E] max-w-[200px] truncate">
                          {response.responses[q.question_id] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E0D8]">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border border-[#E5E0D8] disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-[#5C635A]">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 rounded border border-[#E5E0D8] disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white rounded-xl border border-[#E5E0D8] p-6">
    <div className="flex items-center gap-3 mb-2">
      {icon}
      <span className="text-sm text-[#5C635A]">{label}</span>
    </div>
    <div className="text-2xl font-bold text-[#2C302E]">{value}</div>
  </div>
);

export default SurveyAnalytics;
